import React, { useState, useEffect } from 'react';
import {
    LuSearch,
    LuChevronRight,
    LuChevronDown,
    LuX,
    LuCheck,
    LuFolderTree,
    LuPackage,
    LuTrash2,
    LuPlus,
    LuMinus
} from 'react-icons/lu';

const CategorySelector = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategories, setSelectedCategories] = useState([]); // تغییر به آرایه
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [showModal, setShowModal] = useState(false);

    // دریافت دسته‌بندی‌ها از API
    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await fetch('http://localhost:9001/api/v1/categories');
            const result = await response.json();

            if (result.statusCode === 200) {
                setCategories(result.data);
                const firstLevelIds = result.data.map(cat => cat.id);
                setExpandedNodes(new Set(firstLevelIds));
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setLoading(false);
        }
    };

    // جستجو در دسته‌بندی‌ها
    const searchCategories = (items, term) => {
        if (!term.trim()) return items;

        const results = [];

        const searchInTree = (node, path = []) => {
            const currentPath = [...path, node];

            if (node.name.includes(term)) {
                results.push({
                    category: node,
                    path: currentPath
                });
            }

            if (node.children && node.children.length > 0) {
                node.children.forEach(child => searchInTree(child, currentPath));
            }
        };

        items.forEach(item => searchInTree(item));
        return results;
    };

    // تغییر وضعیت باز/بسته شدن گره
    const toggleNode = (nodeId) => {
        const newExpanded = new Set(expandedNodes);
        if (newExpanded.has(nodeId)) {
            newExpanded.delete(nodeId);
        } else {
            newExpanded.add(nodeId);
        }
        setExpandedNodes(newExpanded);
    };

    // بررسی اینکه آیا دسته انتخاب شده است
    const isCategorySelected = (categoryId) => {
        return selectedCategories.some(cat => cat.category.id === categoryId);
    };

    // اضافه یا حذف دسته
    const toggleCategory = (category, path = []) => {
        const isSelected = isCategorySelected(category.id);

        if (isSelected) {
            // حذف دسته
            setSelectedCategories(selectedCategories.filter(cat => cat.category.id !== category.id));
        } else {
            // اضافه کردن دسته
            setSelectedCategories([...selectedCategories, { category, path }]);
        }
    };

    // حذف یک دسته از لیست انتخاب شده
    const removeSelectedCategory = (categoryId) => {
        setSelectedCategories(selectedCategories.filter(cat => cat.category.id !== categoryId));
    };

    // حذف همه دسته‌های انتخاب شده
    const clearAllCategories = () => {
        setSelectedCategories([]);
    };

    // رندر درخت دسته‌بندی
    const renderCategoryTree = (items, level = 0, parentPath = []) => {
        return items.map((category) => {
            const isExpanded = expandedNodes.has(category.id);
            const hasChildren = category.children && category.children.length > 0;
            const currentPath = [...parentPath, category];
            const isSelected = isCategorySelected(category.id);

            return (
                <div key={category.id} className="select-none">
                    <div
                        className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200 cursor-pointer group
              ${isSelected
                                ? 'bg-blue-50 border-2 border-blue-500 shadow-sm'
                                : 'hover:bg-gray-50 border-2 border-transparent hover:border-gray-200'}`}
                        style={{ marginLeft: `${level * 20}px` }}
                    >
                        <div className="flex items-center gap-3 flex-1" onClick={() => toggleCategory(category, currentPath)}>
                            {hasChildren && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleNode(category.id);
                                    }}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                >
                                    {isExpanded ?
                                        <LuChevronDown size={18} className="text-gray-600" /> :
                                        <LuChevronRight size={18} className="text-gray-600" />
                                    }
                                </button>
                            )}
                            {!hasChildren && <div className="w-7" />}

                            <div className="flex-1">
                                <div className="font-medium text-gray-900">{category.name}</div>
                                {category.description && (
                                    <div className="text-xs text-gray-500 mt-0.5">{category.description}</div>
                                )}
                            </div>

                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 group-hover:border-blue-400'}`}>
                                {isSelected && <LuCheck size={12} className="text-white" />}
                            </div>
                        </div>
                    </div>

                    {hasChildren && isExpanded && (
                        <div className="mt-1 mx-4">
                            {renderCategoryTree(category.children, level + 1, currentPath)}
                        </div>
                    )}
                </div>
            );
        });
    };

    const searchResults = searchCategories(categories, searchTerm);

    // آمار دسته‌بندی‌ها
    const getTotalCategoriesCount = (items) => {
        let count = items.length;
        items.forEach(item => {
            if (item.children && item.children.length > 0) {
                count += getTotalCategoriesCount(item.children);
            }
        });
        return count;
    };

    const totalCategories = getTotalCategoriesCount(categories);

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-50 to-blue-50 lg:p-8 p-2 rounded-lg">
            {/* هدر اصلی */}
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full shadow-sm mb-4">
                        <LuFolderTree className="text-blue-600" size={24} />
                        <span className="text-gray-700 font-medium">ساختار دسته‌بندی محصولات</span>
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-3">
                        انتخاب دسته‌بندی محصولات
                    </h1>
                    <p className="text-gray-600 text-lg">
                        از بین {categories.length} دسته اصلی و {totalCategories} زیردسته انتخاب کنید
                    </p>
                </div>

                {/* کارت اصلی - دو ستونه */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* سمت راست: دسته‌بندی‌ها */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                            {/* نوار جستجو */}
                            <div className="p-6 border-b border-gray-200">
                                <div className="relative">
                                    <LuSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                    <input
                                        type="text"
                                        placeholder="جستجوی دسته‌بندی..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full pr-10 pl-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <LuX size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* محتوای اصلی */}
                            <div className="p-2 lg:p-6">
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                                        <p className="text-gray-500">در حال بارگذاری دسته‌بندی‌ها...</p>
                                    </div>
                                ) : searchTerm ? (
                                    // نتایج جستجو
                                    <div>
                                        <div className="mb-4">
                                            <p className="text-gray-600">
                                                <span className="font-semibold text-blue-600">{searchResults.length}</span> نتیجه برای "{searchTerm}"
                                            </p>
                                        </div>
                                        <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                            {searchResults.map((result, index) => {
                                                const isSelected = isCategorySelected(result.category.id);
                                                return (
                                                    <div
                                                        key={index}
                                                        className={`p-4 border rounded-xl cursor-pointer transition-all group
                              ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-blue-50'}`}
                                                        onClick={() => toggleCategory(result.category, result.path)}
                                                    >
                                                        <div className="flex items-start justify-between">
                                                            <div className="flex-1">
                                                                <div className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                                                                    {result.category.name}
                                                                </div>
                                                                <div className="text-sm text-gray-500">
                                                                    مسیر: {result.path.map(p => p.name).join(' → ')}
                                                                </div>
                                                            </div>
                                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center
                                ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 group-hover:border-blue-400'}`}>
                                                                {isSelected && <LuCheck size={12} className="text-white" />}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    // نمایش درخت دسته‌بندی
                                    <div className="max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                                        {renderCategoryTree(categories)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* سمت چپ: لیست دسته‌های انتخاب شده */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden sticky top-4">
                            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <LuPackage className="text-blue-600" size={20} />
                                        <h2 className="text-lg font-bold text-gray-900">
                                            دسته‌های انتخاب شده
                                        </h2>
                                    </div>
                                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                        {selectedCategories.length}
                                    </span>
                                </div>
                            </div>

                            <div className="p-4 max-h-[500px] overflow-y-auto">
                                {selectedCategories.length === 0 ? (
                                    <div className="text-center py-8">
                                        <LuFolderTree className="text-gray-300 text-5xl mx-auto mb-3" />
                                        <p className="text-gray-400 text-sm">
                                            هیچ دسته‌ای انتخاب نشده
                                        </p>
                                        <p className="text-gray-400 text-xs mt-1">
                                            از سمت راست دسته‌ها را انتخاب کنید
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {selectedCategories.map((item, index) => (
                                            <div
                                                key={item.category.id}
                                                className="group bg-gray-50 rounded-lg p-3 hover:bg-blue-50 transition-all duration-200"
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex-1">
                                                        <div className="font-medium text-gray-900 text-sm">
                                                            {item.category.name}
                                                        </div>
                                                        {item.path.length > 1 && (
                                                            <div className="text-xs text-gray-500 mt-1 truncate">
                                                                {item.path.slice(0, -1).map(p => p.name).join(' → ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={() => removeSelectedCategory(item.category.id)}
                                                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-all"
                                                    >
                                                        <LuX size={14} className="text-red-500" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {selectedCategories.length > 0 && (
                                <div className="p-4 border-t border-gray-200 bg-gray-50">
                                    <button
                                        onClick={clearAllCategories}
                                        className="w-full px-4 py-2 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-all flex items-center justify-center gap-2"
                                    >
                                        <LuTrash2 size={16} />
                                        حذف همه ({selectedCategories.length})
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* فوتر با دکمه تأیید نهایی */}
                {selectedCategories.length > 0 && (
                    <div className="mt-6 bg-white rounded-2xl shadow-xl p-6">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">تعداد دسته‌های انتخاب شده</p>
                                <p className="font-semibold text-gray-900 text-2xl">
                                    {selectedCategories.length} دسته
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    alert(`${selectedCategories.length} دسته با موفقیت انتخاب شد:\n${selectedCategories.map(c => c.category.name).join('\n')}`);
                                }}
                                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-medium hover:shadow-lg transition-all hover:scale-105 transform flex items-center gap-2"
                            >
                                <LuCheck size={20} />
                                تأیید نهایی انتخاب
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* استایل اسکرول بار سفارشی */}
            <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #bfdbfe;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4c8bdb;
        }
      `}</style>
        </div>
    );
};

export default CategorySelector;